import { useEffect, useState } from "react";
import { Loader2, Coins, Ticket } from "lucide-react";
import { toast } from "sonner";
import { api, fmtMoney, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, StatusBadge } from "@/pages/AppLayout";
import { Input } from "@/components/ui/input";

export function useCatalog() {
  const [cat, setCat] = useState(null);
  useEffect(() => { api.get("/billing/catalog").then((r) => setCat(r.data)); }, []);
  return cat;
}

export async function goCheckout(item_id, setBusy, provider = "stripe", coupon) {
  setBusy(`${item_id}:${provider}`);
  try {
    const { data } = await api.post("/payments/checkout", { item_id, provider, coupon: coupon || null, origin_url: window.location.origin });
    window.location.href = data.checkout_url;
  } catch (e) {
    toast.error(formatError(e));
    setBusy(null);
  }
}

export function ProvidersStatus({ cat }) {
  return (
    <div className="flex flex-wrap gap-2">
      {Object.entries(cat.providers).map(([id, p]) => (
        <StatusBadge key={id} ok={p.configured} testId={`provider-status-${id}`}>{p.label}: {p.configured ? (p.mode === "test" ? "modo de prueba" : "activo") : "no configurado"}</StatusBadge>
      ))}
      {!cat.payments_enabled && <StatusBadge testId="payments-disabled">Pagos desactivados</StatusBadge>}
    </div>
  );
}

function Redeem({ onDone }) {
  const [code, setCode] = useState("");
  const redeem = async () => {
    try { const { data } = await api.post("/billing/redeem", { code }); toast.success(`+${data.credits_added} créditos`); setCode(""); onDone(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Card className="flex flex-col sm:flex-row sm:items-center gap-3 mt-10">
      <Ticket className="w-5 h-5 text-emerald-400" />
      <p className="text-sm text-slate-300 flex-1">¿Tienes un cupón de créditos?</p>
      <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="CÓDIGO" className="sm:w-48 bg-[#0A0D14] font-mono" data-testid="redeem-code-input" />
      <button onClick={redeem} disabled={!code} className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 disabled:opacity-50 text-white text-sm" data-testid="redeem-btn">Canjear</button>
    </Card>
  );
}

export default function Credits() {
  const { user, refreshUser } = useAuth();
  const cat = useCatalog();
  const [busy, setBusy] = useState(null);

  return (
    <div data-testid="credits-page">
      <PageHeader eyebrow="Créditos" title="Créditos disponibles" subtitle="Cada operación de IA muestra su costo estimado y se cobra según el trabajo real realizado." action={cat && <ProvidersStatus cat={cat} />} />
      <Card className="relative overflow-hidden mb-10">
        <div className="absolute inset-0 glow-top opacity-60" />
        <div className="relative flex flex-col sm:flex-row sm:items-center justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Saldo actual</p>
            <p className="font-display text-6xl font-bold text-white mt-2" data-testid="credits-balance">{user.credits}</p>
          </div>
          {user.credits === 0 && <p className="text-sm text-amber-300 max-w-xs" data-testid="no-credits-note">Las cuentas nuevas no incluyen créditos gratuitos. Compra un paquete o contrata un plan para empezar a construir.</p>}
        </div>
      </Card>
      <h2 className="text-lg font-medium text-white mb-5">Comprar créditos adicionales</h2>
      <div className="grid md:grid-cols-3 gap-4">
        {cat?.packs.map((p) => (
          <Card key={p.id} className="hover:border-blue-500/40 transition-colors" data-testid={`pack-${p.id}`}>
            <Coins className="w-5 h-5 text-amber-400" />
            <p className="mt-4 font-display text-3xl font-bold text-white">{p.credits} <span className="text-base font-normal text-slate-400">créditos</span></p>
            <p className="mt-1 text-slate-300">{fmtMoney(p.price, cat.currency)} <span className="text-xs text-slate-500">pago único</span></p>
            <div className="mt-6 space-y-2">
              {Object.entries(cat.providers).map(([prov, info]) => (
                <button key={prov} onClick={() => goCheckout(p.id, setBusy, prov)} disabled={!!busy || !info.configured || !cat.payments_enabled} data-testid={`buy-pack-${p.id}-${prov}`}
                  className={`w-full h-10 rounded-xl text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-40 ${prov === "stripe" ? "bg-blue-500 hover:bg-blue-400 text-white" : "border border-[#222B40] text-slate-200"}`}>
                  {busy === `${p.id}:${prov}` && <Loader2 className="w-4 h-4 animate-spin" />}Pagar con {info.label}{!info.configured && " (no configurado)"}
                </button>
              ))}
            </div>
          </Card>
        ))}
      </div>
      <Redeem onDone={refreshUser} />
    </div>
  );
}
